import React, { ComponentProps } from 'react';
import { DefaultView, DeleteAction, OptionalEditView, Views } from '../../types/BlockWithViews';
import {
  BlockInteractions,
  Views as BlockInteractionsViews,
  createButtonObject,
  DISPLAY_TYPE,
} from '../../components/organisms/BlockInteractions';
import { asFetchableBlock, useBlockContext, useRefreshBlock } from '../../_lib/BlockFetcher';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { Icons } from '../../components/atoms/SVG';
import { BlockType } from '../../types/api/ElementType';
import { ContentImageDefaultView } from './views/ContentImageDefaultView';
import { ContentImageEditView } from './views/ContentImageEditView';
import { useContentWrapperContext } from '../../components/blocks/ContentWrapper/ContentWrapperContext';
import { useContentWrapperDndContext } from '../../components/blocks/ContentWrapper/ContentWrapperDndProvider';
import { useLayoutGroupContext } from '../../components/blocks/LayoutGroup/LayoutGroup';
import { headlineHierarchy } from '../../components/organisms/BlockInteractions/BlockInteractions.utils';

type ContentImageProps = Views<
  DefaultView<ContentImageDefaultView> & OptionalEditView<ContentImageEditView> & DeleteAction
>;
function ContentImage({ activeView = 'default', views }: ContentImageProps) {
  const { t } = useSonTranslation('ContentImage');
  const { uuid, elementType } = useBlockContext();
  const { refresh, refreshCount } = useRefreshBlock();

  const contentWrapperContext = useContentWrapperContext();
  const contentWrapperDndContext = useContentWrapperDndContext();
  const layoutGroupProps = useLayoutGroupContext();

  const buttons = {
    ...(views.edit &&
      createButtonObject(
        BlockInteractionsViews.EDIT,
        Icons.EDIT_PENCIL,
        'ContentImage:edit',
        DISPLAY_TYPE.COLLAPSIBLE,
        <ContentImageEditView {...views.edit} />
      )),
    ...(views.delete &&
      createButtonObject(BlockInteractionsViews.DELETE, Icons.ERASER, 'ContentImage:delete', DISPLAY_TYPE.MODAL, null)),
  };

  return (
    <BlockInteractions
      uuid={uuid}
      elementType={elementType}
      headline={{
        text: t('headline'),
        hierarchy: headlineHierarchy(2, contentWrapperContext?.parentIndex, contentWrapperContext?.grandParentIndex),
        visible: true,
      }}
      views={views}
      activeView={activeView}
      buttons={buttons}
      contentWrapperContext={contentWrapperContext}
      contentWrapperDndContext={contentWrapperDndContext}
      isNestedInCollapsibleOrLayoutGroup={!!layoutGroupProps}
      refreshBlock={refresh}
      refreshCount={refreshCount}
    >
      <ContentImageDefaultView {...views.default} />
    </BlockInteractions>
  );
}

export const ContentImageBlock = asFetchableBlock(ContentImage, BlockType.contentimage);
export type ContentImageBlock = ComponentProps<typeof ContentImageBlock>;
